import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { queryClient } from '../lib/queryClient';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // api.js fires this when any request comes back 401 (expired token).
  useEffect(() => {
    const onExpired = () => {
      setToken(null);
      setUser(null);
      toast.error('Your session has expired', { description: 'Please sign in again.' });
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  const fetchCurrentUser = async () => {
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    // Only an auth error means the token is bad. A network blip or a
    // restarting backend should not sign everyone out, so retry those.
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        setLoading(false);
        return;
      } catch (error) {
        const status = error.response?.status;
        if (status === 401 || status === 403 || status === 404) {
          logout();
          setLoading(false);
          return;
        }
        if (attempt < 2) await wait(1500 * (attempt + 1));
      }
    }
    console.error('Could not reach the server to restore the session');
    setUser(null);
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
      email,
      password
    });
    const { token: newToken, user: userData } = response.data;
    queryClient.clear();
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, userData);
    return response.data;
  };

  const logout = () => {
    queryClient.clear();
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isMediaHead: user?.role === 'media_head' || user?.role === 'admin',
    isTeamMember: user?.role === 'team_member'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
